import { useState } from "react"
import { AlertCircle, ArrowLeft, Check, FileText, Info, Pencil, Tag, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PriceRail, RAIL_BAR_CLEARANCE } from "./PriceRail"
import RequestAlert from "@/components/tuw/hire/RequestAlert"
import { MANAGED_TIERS } from "@/lib/post/options"
import { cn } from "@/lib/utils"

// ReviewSummary - the final stage before submit (CONFIG-SCHEMA §1 "review").
//
// Summarizes the choices, surfaces the discount field, the always-on
// free-to-post / pay-on-attach framing, and the file-upload-deferred caveat,
// then submits. The server runs StepValidations + create_order and REDIRECTS to
// /projects on success (that redirect IS the posted confirmation); on 422 it
// returns errors that land in submitErrors and keep the customer here.
//
// Per the design's review screen each summarized group is its own card with an
// "Edit" jump-back link in its header. Service cards jump into that service's
// wizard (onEditService(queueIndex)); the Management & review card jumps into the
// add-on step (onEditManagement). Both reuse the page's hub-and-spoke navigation
// (returnStage = "review"), so finishing the edit lands the customer back here.
//
// Props:
//   state            - the wizard state (projectName, path, managed/QA/QC, discount)
//   summaryRows      - [{ key, name, detail, editIndex? }] one per configured service
//                      (built by the page). editIndex present = jump target into the
//                      service queue; absent = a bundle-included deliverable (not editable).
//   eligibleQaQc     - bool; the QA/QC rows show only when the selection is QA/QC-eligible
//                      (same gate the add-on step uses)
//   railProps        - PriceRail payload (server-reconciled total)
//   fileNotice       - string | null; the file-upload-deferred caveat when files were captured
//   submitting       - bool
//   submitErrors     - { field: message } | null
//   submitIssues     - [{ message, service, service_label, field }]
//   finishProfile    - string | null; the finish-profile path a submit refused
//                      for missing billing details names (shown with the error)
//   onBack / onSubmit
//   onEditService(queueIndex) - jump back into a service's wizard
//   onEditManagement()        - jump back into the Management & review add-on step
//   onApplyDiscount(code) / onRemoveDiscount  - async; the page re-prices
//   discountBusy / discountError

// The managed-tier label is backend-owned (options.MANAGED_TIERS mirrors the Rails
// enum); reuse it so the review reads the SAME label the add-on step showed.
const TIER_LABEL = Object.fromEntries(MANAGED_TIERS.map((t) => [t.value, t.label]))

function SummaryCard({ title, action, children }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
        <h3 className="font-display text-[13.5px] font-bold tracking-tight text-foreground">{title}</h3>
        {action}
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  )
}

// The shared "Edit" jump-back affordance (design: jf-revsec .rh .edit) - muted,
// brightens on hover.
function EditLink({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex shrink-0 items-center gap-1.5 text-[12px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
    >
      <Pencil className="size-3.5" strokeWidth={2} />
      Edit
    </button>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border px-5 py-3 last:border-b-0">
      <span className="text-[12.5px] font-semibold text-foreground">{label}</span>
      <span className="text-right text-[12.5px] text-muted-foreground">{value}</span>
    </div>
  )
}

// A Management & review value: "Added" (foreground) with an optional "· included"
// note when Managed bundles it in; "Not added" stays muted. Mirrors the design.
function AddonValue({ added, included }) {
  if (!added) return "Not added"
  return (
    <span className="font-semibold text-foreground">
      Added{included ? <span className="font-normal text-muted-foreground"> · included</span> : null}
    </span>
  )
}

export function ReviewSummary({
  state,
  summaryRows = [],
  eligibleQaQc = false,
  showManagement = true,
  railProps,
  fileNotice,
  submitting = false,
  submitErrors,
  submitIssues = [],
  finishProfile = null,
  onBack,
  onSubmit,
  onEditService = () => {},
  onFixIssue = () => {},
  fixTargetFor = () => null,
  onEditManagement = () => {},
  onApplyDiscount,
  onRemoveDiscount,
  discountBusy = false,
  discountError,
}) {
  const [code, setCode] = useState("")

  const discount = state.discount
  const errorList = submitErrors ? Object.values(submitErrors).filter(Boolean) : []
  const issueList = submitIssues.length
    ? submitIssues
    : errorList.map((message) => ({ message }))

  const tier = state.managed_services_tier || "self_managed"
  const managed = tier === "managed"
  const qaAdded = managed || state.quality_assurance_addon === "selected"
  const qcAdded = managed || state.quality_control_enabled === "selected"

  // Editable rows jump into a service; rows without an editIndex are bundle
  // deliverables that are derived/included, not separately configured.
  const editableRows = summaryRows.filter((r) => r.editIndex != null)
  const includedRows = summaryRows.filter((r) => r.editIndex == null)

  const apply = () => {
    if (!code.trim()) return
    onApplyDiscount(code.trim())
  }

  // S-POST-REVIEW owns its own grid and sticky wrapper (it has no WizardShell
  // and no stepper), so it carries the same 1081 boundary and bar clearance so
  // the Back / Post project row never sits under the fixed price bar.
  return (
    <div
      className={cn(
        "grid items-start gap-6 min-[1081px]:grid-cols-[minmax(0,1fr)_320px]",
        RAIL_BAR_CLEARANCE
      )}
    >
      <div className="flex min-w-0 flex-col gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Review &amp; post</div>
          <h2 className="mt-1 font-display text-[20px] font-bold tracking-tight text-foreground">
            One last look before you post
          </h2>
          <p className="mt-1 max-w-[560px] text-[13px] leading-relaxed text-muted-foreground text-pretty">
            Posting is free. Nothing is charged until you choose a freelancer and start the work.
          </p>
        </div>

        <SummaryCard title="Project">
          <Row label="Name" value={state.projectName || "Untitled project"} />
          <Row label="Path" value={state.path === "bundle" ? `Bundle · ${state.bundleTier}` : "Build your own"} />
        </SummaryCard>

        {editableRows.map((row) => (
          <SummaryCard
            key={row.key}
            title={row.name}
            action={<EditLink onClick={() => onEditService(row.editIndex)} />}
          >
            {row.fields && row.fields.length > 0 ? (
              row.fields.map((f, i) => <Row key={i} label={f.label} value={f.value} />)
            ) : (
              <div className="px-5 py-3 text-[12.5px] text-muted-foreground">{row.detail || "Configured"}</div>
            )}
          </SummaryCard>
        ))}

        {includedRows.length > 0 && (
          <SummaryCard title="Included with your bundle">
            {includedRows.map((row) => (
              <Row key={row.key} label={row.name} value={row.detail || "Included"} />
            ))}
          </SummaryCard>
        )}

        {editableRows.length === 0 && includedRows.length === 0 && (
          <SummaryCard title="Services">
            <p className="px-5 py-4 text-[12.5px] text-muted-foreground">No services configured.</p>
          </SummaryCard>
        )}

        {showManagement && (
          <SummaryCard title="Management & review" action={<EditLink onClick={onEditManagement} />}>
            <Row
              label="Managed Services"
              value={
                <span className="font-semibold text-foreground">
                  {TIER_LABEL[tier] || "No Management"}
                  {tier === "self_managed" && (
                    <span className="font-normal text-muted-foreground"> · self-managed</span>
                  )}
                </span>
              }
            />
            {eligibleQaQc && <Row label="Quality Assurance" value={<AddonValue added={qaAdded} included={managed} />} />}
            {eligibleQaQc && <Row label="Quality Control" value={<AddonValue added={qcAdded} included={managed} />} />}
          </SummaryCard>
        )}

        {/* discount */}
        <div className="overflow-hidden rounded-2xl border border-border bg-card px-5 py-4">
          <div className="flex items-center gap-2 text-[12.5px] font-bold text-foreground">
            <Tag className="size-3.5 text-muted-foreground" strokeWidth={2} />
            Discount code
          </div>

          {discount ? (
            <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-done/40 bg-done/10 px-3.5 py-2.5">
              <span className="flex items-center gap-2 text-[12.5px] font-semibold text-foreground">
                <Check className="size-3.5 text-done" strokeWidth={2.6} />
                {discount.code} applied
                {discount.percentage ? ` (${discount.percentage}% off)` : discount.value ? ` ($${discount.value} off)` : ""}
              </span>
              <button
                type="button"
                onClick={onRemoveDiscount}
                disabled={discountBusy || submitting}
                className="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                aria-label="Remove discount"
              >
                <X className="size-3.5" strokeWidth={2} />
              </button>
            </div>
          ) : (
            <>
              <p className="mt-2 text-[12px] leading-snug text-muted-foreground">
                Discount codes can not be used in conjunction with other discounts or special rates.
              </p>
              <div className="mt-3 flex items-center gap-2.5">
                <Input
                  value={code}
                  placeholder="Enter a code"
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && apply()}
                  className="max-w-[220px]"
                />
                <Button variant="outline" onClick={apply} disabled={discountBusy || submitting || !code.trim()} className="font-semibold">
                  {discountBusy ? "Applying…" : "Apply"}
                </Button>
              </div>
            </>
          )}

          {discountError && (
            <p className="mt-2 flex items-center gap-1.5 text-[12px] font-semibold text-destructive">
              <AlertCircle className="size-3.5" strokeWidth={2.2} />
              {discountError}
            </p>
          )}
        </div>

        {/* caveats */}
        <div className="flex flex-col gap-2.5">
          <div className="flex items-start gap-2.5 rounded-xl border border-border bg-accent px-3.5 py-3 text-[12.5px] leading-relaxed text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0" strokeWidth={1.9} />
            <span>
              Your project posts for free. You review drafts and revisions until you approve, and you only pay once you
              attach a freelancer.
            </span>
          </div>

          {fileNotice && (
            <div className="flex items-start gap-2.5 rounded-xl border border-border bg-accent px-3.5 py-3 text-[12.5px] leading-relaxed text-muted-foreground">
              <FileText className="mt-0.5 size-4 shrink-0" strokeWidth={1.9} />
              <span>{fileNotice}</span>
            </div>
          )}
        </div>

        {finishProfile && <RequestAlert error={errorList.join(" ")} finishProfile={finishProfile} />}

        {!finishProfile && issueList.length > 0 && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3">
            <p className="text-[12.5px] font-bold text-destructive">Please fix the following before posting:</p>
            <ul className="mt-1.5 flex flex-col gap-2">
              {issueList.map((issue, i) => {
                const target = fixTargetFor(issue)
                return (
                  <li key={`${issue.service || "general"}-${issue.field || i}`} className="flex items-start gap-2 text-[12px] font-semibold text-destructive">
                    <AlertCircle className="mt-px size-3.5 shrink-0" strokeWidth={2.2} />
                    <span className="flex-1">{issue.message}</span>
                    {target && (
                      <button
                        type="button"
                        onClick={() => onFixIssue(issue)}
                        className="shrink-0 text-foreground underline underline-offset-2 hover:text-destructive"
                      >
                        Fix in {target.label}
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {/* Same ≤560 wrap as the wizard footer (design `.jf-foot`). */}
        <div className="flex items-center gap-3 max-[561px]:flex-wrap max-[561px]:gap-y-2.5">
          <Button variant="outline" onClick={onBack} className="gap-1.5">
            <ArrowLeft className="size-4" strokeWidth={2} />
            Back
          </Button>
          <div className="flex-1" />
          <Button onClick={onSubmit} disabled={submitting} className="gap-1.5 font-bold">
            {submitting ? "Posting…" : "Post project"}
          </Button>
        </div>
      </div>

      <div className="min-[1081px]:sticky min-[1081px]:top-4">
        <PriceRail {...railProps} />
      </div>
    </div>
  )
}
